import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  Edit,
  Clock,
  MapPin,
  DollarSign,
  Users,
  Calendar as CalendarIcon,
  FileText,
  Search,
  Upload,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { businessService, BusinessServiceItemDto, BusinessHourDto, ClosedDate, BusinessFaqDto } from "@/services/businessService";
import { StepBusinessSearch, Business } from "@/components/onboarding/StepBusinessSearch";
import { StepBusinessConfirm } from "@/components/onboarding/StepBusinessConfirm";

// Type alias is preferred for empty extensions
type Service = BusinessServiceItemDto;

interface WeeklyHours {
  [key: string]: BusinessHourDto;
}

interface FAQ extends BusinessFaqDto {
    id: string; // Enforce id for UI key
}

export default function YourBusiness() {
  const { toast } = useToast();
  const [services, setServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<Service[]>([]);
  
  // Overview State
  const [ownerName, setOwnerName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessCategory, setBusinessCategory] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [about, setAbout] = useState("");

  // Location Rules State
  const [parkingInfo, setParkingInfo] = useState("");
  const [accessibilityNotes, setAccessibilityNotes] = useState("");
  const [directions, setDirections] = useState("");
  const [suiteInstructions, setSuiteInstructions] = useState("");
  const [deliveryRadius, setDeliveryRadius] = useState("");
  const [deliveryRadiusUnit, setDeliveryRadiusUnit] = useState("miles");
  const [deliveryFee, setDeliveryFee] = useState("");

  // Payment Policies State
  const [requireDeposit, setRequireDeposit] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [cancellationFee, setCancellationFee] = useState("");
  const [noShowFee, setNoShowFee] = useState("");

  // New Service/Product State
  const [newServiceName, setNewServiceName] = useState("");
  const [newServicePrice, setNewServicePrice] = useState("");
  const [newServiceDuration, setNewServiceDuration] = useState("");
  const [newServiceCategory, setNewServiceCategory] = useState("");
  const [isAddServiceOpen, setIsAddServiceOpen] = useState(false);

  const [newProductName, setNewProductName] = useState("");
  const [newProductPrice, setNewProductPrice] = useState("");
  const [newProductCategory, setNewProductCategory] = useState("");
  const [newProductDescription, setNewProductDescription] = useState("");
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [isEditProductOpen, setIsEditProductOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Service | null>(null);
  const [productCategoryFilter, setProductCategoryFilter] = useState("");
  const [isCreatingNewCategory, setIsCreatingNewCategory] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [bulkImportText, setBulkImportText] = useState("");
  const [isBulkImporting, setIsBulkImporting] = useState(false);

  // Closed Date Exception State
  const [exceptionReason, setExceptionReason] = useState("");

  // Google Update State
  const [isGoogleUpdateOpen, setIsGoogleUpdateOpen] = useState(false);
  const [googleStep, setGoogleStep] = useState<'search' | 'confirm'>('search');
  const [selectedGoogleBusiness, setSelectedGoogleBusiness] = useState<Business | null>(null);

  const sophiePhoneNumber = "+1 (438) 230-2029";
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [newFaqQuestion, setNewFaqQuestion] = useState("");
  const [newFaqAnswer, setNewFaqAnswer] = useState("");
  const [isAddFaqOpen, setIsAddFaqOpen] = useState(false);

  const [weeklyHours, setWeeklyHours] = useState<WeeklyHours>({
    Monday: { enabled: true, open: "09:00", close: "17:00" },
    Tuesday: { enabled: true, open: "09:00", close: "17:00" },
    Wednesday: { enabled: true, open: "09:00", close: "17:00" },
    Thursday: { enabled: true, open: "09:00", close: "17:00" },
    Friday: { enabled: true, open: "09:00", close: "17:00" },
    Saturday: { enabled: true, open: "10:00", close: "16:00" },
    Sunday: { enabled: false, open: "09:00", close: "17:00" },
  });

  const [closedDates, setClosedDates] = useState<Date[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [paymentTypes, setPaymentTypes] = useState<string[]>([
    "Credit Card",
    "Debit Card",
    "Cash",
    "Apple Pay",
  ]);
  const [newPaymentType, setNewPaymentType] = useState("");
  const [isAddPaymentTypeOpen, setIsAddPaymentTypeOpen] = useState(false);

  const loadBusinessInfo = useCallback(async () => {
    try {
      const info = await businessService.getBusinessInfo();
      if (info) {
        setOwnerName(info.ownerName || "");
        setBusinessName(info.businessName || "");
        setBusinessCategory(info.businessCategory || "");
        setBusinessPhone(info.businessPhone || "");
        setAddress(info.address || "");
        setWebsite(info.website || "");
        setEmail(info.email || "");
        setAbout(info.about || "");
        
        setParkingInfo(info.parkingInfo || "");
        setAccessibilityNotes(info.accessibilityNotes || "");
        setDirections(info.directions || "");
        setSuiteInstructions(info.suiteInstructions || "");
        setDeliveryRadius(info.deliveryRadius?.toString() || "");
        setDeliveryRadiusUnit(info.deliveryRadiusUnit || "miles");
        setDeliveryFee(info.deliveryFee?.toString() || "");

        setRequireDeposit(info.requireDeposit || false);
        setDepositAmount(info.depositAmount?.toString() || "");
        setCancellationFee(info.cancellationFee?.toString() || "");
        setNoShowFee(info.noShowFee?.toString() || "");
        
        if (info.acceptedPaymentTypes && info.acceptedPaymentTypes.length > 0) {
            setPaymentTypes(info.acceptedPaymentTypes);
        }

        if (info.closedDates) {
            setClosedDates(info.closedDates.map(d => new Date(d.date)));
        }

        if (info.services) setServices(info.services);
        if (info.products) setProducts(info.products);
        if (info.hours && Object.keys(info.hours).length > 0) {
             // Merge with defaults to ensure all days exist
             setWeeklyHours(prev => ({ ...prev, ...info.hours }));
        }
        if (info.faqs) {
            setFaqs(info.faqs.map(f => ({ ...f, id: f.id || Date.now().toString() })));
        }
      }
    } catch (error) {
      console.error("Failed to load business info", error);
      toast({
        title: "Error",
        description: "Failed to load business information",
        variant: "destructive",
      });
    }
  }, [toast]);

  useEffect(() => {
    loadBusinessInfo();
  }, [loadBusinessInfo]);

  const handleGoogleSelect = (business: Business) => {
      setSelectedGoogleBusiness(business);
      setGoogleStep('confirm');
  };

  const handleGoogleConfirm = async () => {
      if (!selectedGoogleBusiness) return;
      const b = selectedGoogleBusiness;

      // Update local state
      setBusinessName(b.name);
      if (b.categories && b.categories.length > 0) {
          setBusinessCategory(b.categories[0]);
      }
      setAddress(b.address);
      if (b.phone) setBusinessPhone(b.phone);
      if (b.website) setWebsite(b.website);
      
      const newAbout = `Google Rating: ${b.rating} (${b.reviewCount} reviews)`;
      setAbout(newAbout);

      if (b.hours) {
          setWeeklyHours(prev => ({ ...prev, ...b.hours }));
      }

      // Save to backend
      try {
          await businessService.updateBusinessInfo({
              businessName: b.name,
              businessCategory: b.categories[0] || "",
              address: b.address,
              businessPhone: b.phone,
              website: b.website,
              about: newAbout
          });
          
          if (b.hours) {
              await businessService.updateBusinessHours(b.hours);
          }

          toast({ title: "Success", description: "Business information updated from Google" });
      } catch (e) {
          toast({ title: "Error", description: "Failed to save updated information", variant: "destructive" });
      }

      setIsGoogleUpdateOpen(false);
      setGoogleStep('search');
      setSelectedGoogleBusiness(null);
  };

  const saveOverview = async () => {
    try {
        await businessService.updateBusinessInfo({
            ownerName,
            businessName,
            businessCategory,
            businessPhone,
            address,
            website,
            email,
            about
        });
        toast({ title: "Success", description: "Business overview saved" });
    } catch (e) {
        toast({ title: "Error", description: "Failed to save overview", variant: "destructive" });
    }
  };

  const saveHours = async () => {
      try {
          await businessService.updateBusinessHours(weeklyHours);
          toast({ title: "Success", description: "Business hours saved" });
      } catch (e) {
          toast({ title: "Error", description: "Failed to save hours", variant: "destructive" });
      }
  };

  const saveLocationRules = async () => {
      try {
          await businessService.updateBusinessInfo({
              parkingInfo,
              accessibilityNotes,
              directions,
              suiteInstructions,
              deliveryRadius: deliveryRadius ? parseFloat(deliveryRadius) : null,
              deliveryRadiusUnit,
              deliveryFee: deliveryFee ? parseFloat(deliveryFee) : null
          });
          toast({ title: "Success", description: "Location rules saved" });
      } catch (e) {
          toast({ title: "Error", description: "Failed to save location rules", variant: "destructive" });
      }
  };

  const savePaymentPolicies = async () => {
      try {
          await businessService.updateBusinessInfo({
              acceptedPaymentTypes: paymentTypes,
              requireDeposit,
              depositAmount: depositAmount ? parseFloat(depositAmount.replace('$','')) : null,
              cancellationFee: cancellationFee ? parseFloat(cancellationFee.replace('$','')) : null,
              noShowFee: noShowFee ? parseFloat(noShowFee.replace('$','')) : null
          });
          toast({ title: "Success", description: "Payment policies saved" });
      } catch (e) {
          toast({ title: "Error", description: "Failed to save payment policies", variant: "destructive" });
      }
  };

  const addService = async () => {
      try {
          const newService = await businessService.addService({
              name: newServiceName,
              price: newServicePrice,
              duration: newServiceDuration,
              category: newServiceCategory,
              inStock: true
          });
          setServices([...services, newService]);
          setNewServiceName("");
          setNewServicePrice("");
          setNewServiceDuration("");
          setNewServiceCategory("");
          setIsAddServiceOpen(false);
          toast({ title: "Success", description: "Service added" });
      } catch (e) {
          toast({ title: "Error", description: "Failed to add service", variant: "destructive" });
      }
  };

  const deleteService = async (id: string) => {
    try {
        await businessService.deleteService(id);
        setServices(services.filter((s) => s.id !== id));
        toast({ title: "Success", description: "Service deleted" });
    } catch (e) {
        toast({ title: "Error", description: "Failed to delete service", variant: "destructive" });
    }
  };

  const addProduct = async () => {
      try {
          const newProduct = await businessService.addProduct({
              name: newProductName,
              price: newProductPrice,
              duration: "",
              category: newProductCategory,
              description: newProductDescription,
              inStock: true
          });
          setProducts([...products, newProduct]);
          closeProductDialog();
          toast({ title: "Success", description: "Product added" });
      } catch (e) {
          toast({ title: "Error", description: "Failed to add product", variant: "destructive" });
      }
  };

  const deleteProduct = async (id: string) => {
    try {
        await businessService.deleteProduct(id);
        setProducts(products.filter((p) => p.id !== id));
        toast({ title: "Success", description: "Product deleted" });
    } catch (e) {
        toast({ title: "Error", description: "Failed to delete product", variant: "destructive" });
    }
  };

  const toggleProductStock = async (id: string) => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    try {
        const updated = await businessService.updateProduct(id, { ...product, inStock: !product.inStock });
        setProducts(products.map((p) => (p.id === id ? updated : p)));
    } catch (e) {
        toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };
  
  const toggleServiceStock = async (id: string) => {
    const service = services.find(s => s.id === id);
    if (!service) return;
    try {
        const updated = await businessService.updateService(id, { ...service, inStock: !service.inStock });
        setServices(services.map((s) => (s.id === id ? updated : s)));
    } catch (e) {
        toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const productCategories = useMemo(
    () => [...new Set(products.map(p => p.category).filter(Boolean))],
    [products]
  );

  const groupedProducts = useMemo(() => {
    const filtered = productCategoryFilter
      ? products.filter(p => p.category === productCategoryFilter)
      : products;
    return filtered.reduce<Record<string, Service[]>>((acc, product) => {
      const cat = product.category || "Uncategorized";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(product);
      return acc;
    }, {});
  }, [products, productCategoryFilter]);

  const openEditProduct = (product: Service) => {
    setEditingProduct(product);
    setNewProductName(product.name);
    setNewProductPrice(product.price);
    setNewProductCategory(product.category);
    setNewProductDescription(product.description || "");
    setIsCreatingNewCategory(false);
    setNewCategoryInput("");
    setIsEditProductOpen(true);
  };

  const saveEditProduct = async () => {
    if (!editingProduct?.id) return;
    try {
      const updated = await businessService.updateProduct(editingProduct.id, {
        ...editingProduct,
        name: newProductName,
        price: newProductPrice,
        category: newProductCategory,
        description: newProductDescription,
      });
      setProducts(products.map(p => (p.id === editingProduct.id ? updated : p)));
      closeProductDialog();
      toast({ title: "Success", description: "Product updated" });
    } catch (e) {
      toast({ title: "Error", description: "Failed to update product", variant: "destructive" });
    }
  };

  const closeProductDialog = () => {
    setIsAddProductOpen(false);
    setIsEditProductOpen(false);
    setEditingProduct(null);
    setNewProductName("");
    setNewProductPrice("");
    setNewProductCategory("");
    setNewProductDescription("");
    setIsCreatingNewCategory(false);
    setNewCategoryInput("");
  };

  const handleCategorySelect = (value: string) => {
    if (value === "__new__") {
      setIsCreatingNewCategory(true);
      setNewCategoryInput("");
    } else {
      setIsCreatingNewCategory(false);
      setNewProductCategory(value);
    }
  };

  const confirmNewCategory = () => {
    if (newCategoryInput.trim()) {
      setNewProductCategory(newCategoryInput.trim());
      setIsCreatingNewCategory(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) return;
    setIsBulkImporting(true);
    try {
      const imported = await businessService.bulkImportProducts(bulkImportText);
      setProducts(prev => [...prev, ...imported]);
      setBulkImportText("");
      setIsBulkImportOpen(false);
      toast({
        title: "Success",
        description: `Imported ${imported.length} product${imported.length !== 1 ? "s" : ""}`,
      });
    } catch (e) {
      toast({ title: "Error", description: "Failed to import products", variant: "destructive" });
    } finally {
      setIsBulkImporting(false);
    }
  };

  const addClosedDate = async () => {
      if (selectedDate) {
          const newDates = [...closedDates, selectedDate];
          setClosedDates(newDates);
          
          setExceptionReason("");
          setSelectedDate(undefined);
          // Don't save immediately, wait for user to click Save
          // But maybe we should? The previous code did.
          // User asked for "Save Closed Dates" button so we'll do batch save.
      }
  };
  
  const removeClosedDate = async (dateToRemove: Date) => {
      const newDates = closedDates.filter(d => d !== dateToRemove);
      setClosedDates(newDates);
  };
  
  const saveClosedDates = async () => {
      try {
          // Convert to ClosedDate objects
          const closedDatesDto: ClosedDate[] = closedDates.map(d => ({
              date: d.toISOString(),
              reason: "" // We don't really track reasons per date yet in UI except the input field which is cleared
              // If we want to persist reasons, we need to change how state is stored (Date -> ClosedDate object in state)
              // For now, let's keep it simple as requested
          }));
          
          await businessService.updateBusinessInfo({
              closedDates: closedDatesDto
          });
          toast({ title: "Success", description: "Closed dates saved" });
      } catch (e) {
         toast({ title: "Error", description: "Failed to save closed dates", variant: "destructive" }); 
      }
  };

  const saveFaqs = async () => {
      try {
          await businessService.updateBusinessFaqs(faqs);
          toast({ title: "Success", description: "FAQs saved" });
      } catch (e) {
          toast({ title: "Error", description: "Failed to save FAQs", variant: "destructive" });
      }
  };
  
  const addPaymentType = () => {
      if (newPaymentType) {
          setPaymentTypes([...paymentTypes, newPaymentType]);
          setNewPaymentType("");
          setIsAddPaymentTypeOpen(false);
      }
  };

  const toggleDay = (day: string) => {
    setWeeklyHours({
      ...weeklyHours,
      [day]: { ...weeklyHours[day], enabled: !weeklyHours[day].enabled },
    });
  };

  const updateHours = (day: string, type: "open" | "close", value: string) => {
    setWeeklyHours({
      ...weeklyHours,
      [day]: { ...weeklyHours[day], [type]: value },
    });
  };

  const addFaq = () => {
    if (newFaqQuestion.trim() && newFaqAnswer.trim()) {
      setFaqs([...faqs, {
        id: Date.now().toString(),
        question: newFaqQuestion,
        answer: newFaqAnswer
      }]);
      setNewFaqQuestion("");
      setNewFaqAnswer("");
      setIsAddFaqOpen(false);
    }
  };

  const deleteFaq = (id: string) => {
    setFaqs(faqs.filter(f => f.id !== id));
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      {/* Update via Google Card */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
              <Search className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-blue-900">Auto-Update with Google</h2>
              <p className="text-blue-700">Sync your business details directly from your Google Business Profile</p>
            </div>
          </div>
          <Dialog open={isGoogleUpdateOpen} onOpenChange={setIsGoogleUpdateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                Find My Business
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Update Business Information</DialogTitle>
                <DialogDescription>
                  Search for your business on Google to automatically update your profile details and hours.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                {googleStep === 'search' ? (
                  <StepBusinessSearch onSelect={handleGoogleSelect} />
                ) : (
                  selectedGoogleBusiness && (
                    <StepBusinessConfirm 
                      business={selectedGoogleBusiness} 
                      onBack={() => setGoogleStep('search')} 
                      onConfirm={handleGoogleConfirm}
                    />
                  )
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </Card>

      {/* Overview Section */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Business Overview</h2>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ownerName">Owner Name</Label>
              <Input id="ownerName" placeholder="e.g. John Doe" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name</Label>
              <Input
                id="businessName"
                placeholder="e.g. Sophie's Salon"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Business Category</Label>
              <Select value={businessCategory} onValueChange={setBusinessCategory}>
                <SelectTrigger id="category">
                  <SelectValue placeholder="e.g. Salon & Spa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salon">Salon & Spa</SelectItem>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="professional">Professional Services</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessPhone">Business Phone</Label>
              <Input
                id="businessPhone"
                type="tel"
                placeholder="e.g. +1 (555) 123-4567"
                value={businessPhone}
                onChange={(e) => setBusinessPhone(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              placeholder="e.g. 123 Main Street, Suite 100, San Francisco, CA 94102"
              rows={3}
              className="resize-none"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                type="url"
                placeholder="e.g. https://sophiessalon.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Business Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="e.g. hello@sophiessalon.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="about">About Your Business</Label>
            <Textarea
              id="about"
              placeholder="e.g. We are a full-service salon offering haircuts, styling, coloring, and beauty treatments. Our experienced team is dedicated to making you look and feel your best."
              rows={4}
              className="resize-none"
              value={about}
              onChange={(e) => setAbout(e.target.value)}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={saveOverview}>Save Overview</Button>
          </div>
        </div>
      </Card>

      {/* Weekly Hours */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Clock className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Weekly Hours</h2>
        </div>

        <div className="space-y-3">
          {Object.entries(weeklyHours).map(([day, hours]) => (
            <div
              key={day}
              className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 sm:w-32">
                <Switch
                  checked={hours.enabled}
                  onCheckedChange={() => toggleDay(day)}
                />
                <Label className="font-medium cursor-pointer">{day}</Label>
              </div>

              {hours.enabled ? (
                <div className="flex items-center gap-3 flex-1">
                  <Input
                    type="time"
                    value={hours.open}
                    onChange={(e) => updateHours(day, "open", e.target.value)}
                    className="w-32"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={hours.close}
                    onChange={(e) => updateHours(day, "close", e.target.value)}
                    className="w-32"
                  />
                </div>
              ) : (
                <span className="text-muted-foreground">Closed</span>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-4">
          <Button onClick={saveHours}>Save Hours</Button>
        </div>
      </Card>

      {/* Services */}
      <Card className="p-6 card-hover">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold">Services</h2>
          </div>

          <Dialog open={isAddServiceOpen} onOpenChange={setIsAddServiceOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Service</DialogTitle>
                <DialogDescription>
                  Create a new service for your business
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="serviceName">Service Name</Label>
                  <Input id="serviceName" placeholder="e.g., Haircut" value={newServiceName} onChange={(e) => setNewServiceName(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="servicePrice">Price</Label>
                    <Input id="servicePrice" placeholder="$45" value={newServicePrice} onChange={(e) => setNewServicePrice(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="serviceDuration">Duration</Label>
                    <Input id="serviceDuration" placeholder="30 min" value={newServiceDuration} onChange={(e) => setNewServiceDuration(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serviceCategory">Category</Label>
                  <Input id="serviceCategory" placeholder="e.g., Hair" value={newServiceCategory} onChange={(e) => setNewServiceCategory(e.target.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={addService}>Add Service</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {services.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            Empty list.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.filter(s => s.category !== "Product").map((service) => (
              <Card key={service.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{service.name}</h3>
                      <Switch checked={service.inStock} onCheckedChange={() => toggleServiceStock(service.id!)} />
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {service.category}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => deleteService(service.id!)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{service.price}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>{service.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Status:</span>
                    <Badge
                      variant={service.inStock ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {service.inStock ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Products */}
      <Card className="p-6 card-hover">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold">Products</h2>
          </div>

          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setBulkImportText(""); setIsBulkImportOpen(true); }}>
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button size="sm" onClick={() => { closeProductDialog(); setIsAddProductOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Category Filter */}
        {productCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            <Button
              size="sm"
              variant={productCategoryFilter === "" ? "default" : "outline"}
              onClick={() => setProductCategoryFilter("")}
            >
              All
            </Button>
            {productCategories.map(cat => (
              <Button
                key={cat}
                size="sm"
                variant={productCategoryFilter === cat ? "default" : "outline"}
                onClick={() => setProductCategoryFilter(cat)}
              >
                {cat}
              </Button>
            ))}
          </div>
        )}

        {products.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            Empty list.
          </div>
        ) : Object.keys(groupedProducts).length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No products match the selected filter.
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
              <div key={category}>
                <h3 className="text-lg font-semibold mb-3">{category}</h3>
                <div className="rounded-lg border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product Name</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead className="hidden md:table-cell">Category</TableHead>
                        <TableHead className="hidden lg:table-cell">Description</TableHead>
                        <TableHead>In Stock</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryProducts.map(product => (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell>{product.price}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Badge variant="secondary" className="text-xs">{product.category}</Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell max-w-[200px] truncate">
                            {product.description || "—"}
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={product.inStock}
                              onCheckedChange={() => toggleProductStock(product.id!)}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditProduct(product)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => deleteProduct(product.id!)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add Product Dialog */}
      <Dialog open={isAddProductOpen} onOpenChange={(open) => { if (!open) closeProductDialog(); else setIsAddProductOpen(true); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
            <DialogDescription>Create a new product for your business</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="productName">Product Name</Label>
              <Input id="productName" placeholder="e.g., Hair Gel" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="productPrice">Price</Label>
              <Input id="productPrice" placeholder="$25" value={newProductPrice} onChange={(e) => setNewProductPrice(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="productCategory">Category</Label>
              {isCreatingNewCategory ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="New category name"
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") confirmNewCategory(); }}
                    onBlur={confirmNewCategory}
                    autoFocus
                  />
                  <Button size="sm" variant="outline" onClick={() => setIsCreatingNewCategory(false)}>Cancel</Button>
                </div>
              ) : (
                <Select value={newProductCategory} onValueChange={handleCategorySelect}>
                  <SelectTrigger id="productCategory">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {productCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    <SelectItem value="__new__">+ Create New Category</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="productDescription">Description</Label>
              <Textarea
                id="productDescription"
                placeholder="e.g., Premium quality hair gel for all hair types"
                rows={3}
                className="resize-none"
                value={newProductDescription}
                onChange={(e) => setNewProductDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={addProduct}>Add Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={isEditProductOpen} onOpenChange={(open) => { if (!open) closeProductDialog(); else setIsEditProductOpen(true); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
            <DialogDescription>Update the product details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editProductName">Product Name</Label>
              <Input id="editProductName" placeholder="e.g., Hair Gel" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editProductPrice">Price</Label>
              <Input id="editProductPrice" placeholder="$25" value={newProductPrice} onChange={(e) => setNewProductPrice(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editProductCategory">Category</Label>
              {isCreatingNewCategory ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="New category name"
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") confirmNewCategory(); }}
                    onBlur={confirmNewCategory}
                    autoFocus
                  />
                  <Button size="sm" variant="outline" onClick={() => setIsCreatingNewCategory(false)}>Cancel</Button>
                </div>
              ) : (
                <Select value={newProductCategory} onValueChange={handleCategorySelect}>
                  <SelectTrigger id="editProductCategory">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {productCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    <SelectItem value="__new__">+ Create New Category</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="editProductDescription">Description</Label>
              <Textarea
                id="editProductDescription"
                placeholder="e.g., Premium quality hair gel for all hair types"
                rows={3}
                className="resize-none"
                value={newProductDescription}
                onChange={(e) => setNewProductDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEditProduct}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={isBulkImportOpen} onOpenChange={(open) => { if (!open) { setIsBulkImportOpen(false); setBulkImportText(""); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Import Products</DialogTitle>
            <DialogDescription>
              Paste your product list, menu, or any text containing product information. Our AI will extract and organize the products automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder={"e.g.\nCheeseburger - $12.99 (Lunch)\nCaesar Salad - $9.50 (Lunch)\nGrilled Salmon - $24.00 (Dinner)\nChocolate Cake - $8.00 (Desserts)"}
              rows={12}
              value={bulkImportText}
              onChange={(e) => setBulkImportText(e.target.value)}
              disabled={isBulkImporting}
            />
          </div>
          <DialogFooter>
            <Button
              onClick={handleBulkImport}
              disabled={!bulkImportText.trim() || isBulkImporting}
            >
              {isBulkImporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Products
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location Rules */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Location Rules</h2>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="parking">Parking Information</Label>
              <Textarea
                id="parking"
                placeholder="e.g. Free parking in rear lot. Street parking also available."
                rows={3}
                className="resize-none"
                value={parkingInfo}
                onChange={(e) => setParkingInfo(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="accessibility">Accessibility Notes</Label>
              <Textarea
                id="accessibility"
                placeholder="e.g. Wheelchair accessible entrance. Elevator to second floor."
                rows={3}
                className="resize-none"
                value={accessibilityNotes}
                onChange={(e) => setAccessibilityNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="directions">Directions from Landmarks</Label>
              <Textarea
                id="directions"
                placeholder="e.g. Located two blocks south of City Hall, across from Central Park."
                rows={3}
                className="resize-none"
                value={directions}
                onChange={(e) => setDirections(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="suiteInstructions">Suite Number Instructions</Label>
              <Textarea
                id="suiteInstructions"
                placeholder="e.g. Suite 100 on the ground floor. Enter through the main lobby."
                rows={3}
                className="resize-none"
                value={suiteInstructions}
                onChange={(e) => setSuiteInstructions(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deliveryRadius">Delivery Radius</Label>
              <div className="flex gap-2">
                <Input
                  id="deliveryRadius"
                  type="number"
                  placeholder="10"
                  className="flex-1"
                  value={deliveryRadius}
                  onChange={(e) => setDeliveryRadius(e.target.value)}
                />
                <Select value={deliveryRadiusUnit} onValueChange={setDeliveryRadiusUnit}>
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="Miles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="miles">Miles</SelectItem>
                    <SelectItem value="km">KM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deliveryFee">Delivery Fee</Label>
              <Input
                id="deliveryFee"
                placeholder="e.g. $5.00"
                value={deliveryFee}
                onChange={(e) => setDeliveryFee(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveLocationRules}>Save Location Rules</Button>
          </div>
        </div>
      </Card>

      {/* Closed Days & Exceptions */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <CalendarIcon className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Closed Days & Exceptions</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Select dates when your business will be closed for holidays or vacation
            </p>
            <Calendar
              mode="multiple"
              selected={closedDates}
              onSelect={(dates) => setClosedDates(dates || [])}
              className="rounded-lg border border-border pointer-events-auto"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Closed Dates</h3>
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Exception
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Closed Date</DialogTitle>
                    <DialogDescription>
                      Add a holiday or vacation day when you'll be closed
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="exceptionName">Reason</Label>
                      <Input
                        id="exceptionName"
                        placeholder="e.g., Christmas Day, Staff Training"
                        value={exceptionReason}
                        onChange={(e) => setExceptionReason(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        className="rounded-lg border border-border pointer-events-auto"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={addClosedDate}>Add Closed Date</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {closedDates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No closed dates scheduled</p>
                <p className="text-sm">Select dates on the calendar to add them</p>
              </div>
            ) : (
              <div className="space-y-2">
                {closedDates.map((date, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  >
                    <span className="text-sm">{date.toLocaleDateString()}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => removeClosedDate(date)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6">
            <Button onClick={saveClosedDates}>Save Closed Dates</Button>
        </div>
      </Card>

      {/* Payment & Billing Policies */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Payment & Billing Policies</h2>
        </div>

        <div className="space-y-6">
          <div className="space-y-3">
            <Label>Accepted Payment Types</Label>
            <div className="flex flex-wrap gap-2">
              {paymentTypes.map((type, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="px-3 py-1.5 cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  {type}
                  <button
                    className="ml-2 text-xs"
                    onClick={() =>
                      setPaymentTypes(paymentTypes.filter((t) => t !== type))
                    }
                  >
                    ×
                  </button>
                </Badge>
              ))}
              <Dialog open={isAddPaymentTypeOpen} onOpenChange={setIsAddPaymentTypeOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Payment Type</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Input placeholder="e.g., Venmo, PayPal" value={newPaymentType} onChange={(e) => setNewPaymentType(e.target.value)} />
                  </div>
                  <DialogFooter>
                    <Button onClick={addPaymentType}>Add Payment Type</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
              <div>
                <Label className="font-medium">Require Deposit</Label>
                <p className="text-sm text-muted-foreground">
                  Require deposit for bookings
                </p>
              </div>
              <Switch checked={requireDeposit} onCheckedChange={setRequireDeposit} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="depositAmount">Deposit Amount</Label>
              <Input id="depositAmount" placeholder="$25.00" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cancellationFee">Cancellation Fee</Label>
              <Input
                id="cancellationFee"
                placeholder="$20.00"
                value={cancellationFee}
                onChange={(e) => setCancellationFee(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Fee for cancellations within 24 hours
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="noShowFee">No-Show Fee</Label>
              <Input id="noShowFee" placeholder="$50.00" value={noShowFee} onChange={(e) => setNoShowFee(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                Fee for missed appointments
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={savePaymentPolicies}>Save Payment Policies</Button>
          </div>
        </div>
      </Card>

      {/* Frequently Asked Questions - Training for Bot */}
      <Card className="p-6 card-hover">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Frequently Asked Questions</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Train Sophie to answer specific questions about your business. Add custom Q&As to ensure Sophie responds exactly how you want to common customer inquiries.
              </p>
            </div>
          </div>
          <Dialog open={isAddFaqOpen} onOpenChange={setIsAddFaqOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add FAQ
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Frequently Asked Question</DialogTitle>
                <DialogDescription>
                  Add a question and answer to train Sophie on how to respond
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="faq-question">Question</Label>
                  <Input
                    id="faq-question"
                    placeholder="e.g., Do you offer gift cards?"
                    value={newFaqQuestion}
                    onChange={(e) => setNewFaqQuestion(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="faq-answer">Answer</Label>
                  <Textarea
                    id="faq-answer"
                    placeholder="e.g., Yes, we offer both physical and digital gift cards in any amount."
                    rows={4}
                    className="resize-none"
                    value={newFaqAnswer}
                    onChange={(e) => setNewFaqAnswer(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={addFaq}>Add FAQ</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        
        {faqs.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No FAQs added yet. Click "Add FAQ" to create one.
          </div>
        ) : (
          <div className="space-y-3">
            {faqs.map((faq) => (
              <Card key={faq.id} className="p-4 border-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Question</p>
                      <p className="font-medium">{faq.question}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Answer</p>
                      <p className="text-sm">{faq.answer}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => deleteFaq(faq.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-6">
            <Button onClick={saveFaqs}>Save FAQs</Button>
        </div>
      </Card>
    </div>
  );
}
